import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import { useI18n } from '@vista-digital/occ-ui'
import type { FunctionComponent } from 'react'
import { memo, useEffect, useMemo } from 'react'
import { createBrowserRouter, createMemoryRouter, useLocation, useNavigate, useNavigationType } from 'react-router-v8'
// React Router v8 exposes the DOM-aware `RouterProvider` (the one `react-router-dom` used to
// re-export) from the `react-router/dom` entry point.
import { RouterProvider } from 'react-router-v8/dom'
import { useScrollRestoration } from '@/common/hooks/useScrollRestoration'
import { useUrlService } from '@/content/services/omnia-service-context'
import { navigationService } from '../content/services/navigation.service'
import { getRoutes } from './getRoutes'
import type { LayoutOverrideProps } from './Layout'
import { Layout } from './Layout'

const logger = getLogger('App')

export interface PagesRouterOverrideProps extends LayoutWrapperOverrideProps {
    overridePageComponent?: {
        pageKey: string
        component: FunctionComponent
    }
    overridePath?: string
    overrideSearchParams?: { [key: string]: string }
}

export const PagesRouter: FunctionComponent<PagesRouterOverrideProps> = ({
    overridePageComponent,
    overridePath,
    overrideSearchParams,
    ...layoutOverrideProps
}) => {
    logger.value.trace('PagesRouter initialised', {
        overridePageComponent,
        overridePath,
        overrideSearchParams,
        ...layoutOverrideProps,
    })
    logger.value.debug('PagesRouter', Date.now() - window.loadMs)

    const i18n = useI18n()
    const urlService = useUrlService()
    urlService.setI18n(i18n)

    // eslint-disable-next-line react-hooks/exhaustive-deps -- This is a one time effect
    const allRoutes = useMemo(() => getRoutes(overridePageComponent), [])
    logger.value.trace('All routes created', allRoutes)

    const LayoutWrapperMemo = useMemo(() => () => <LayoutWrapper {...layoutOverrideProps} />, [layoutOverrideProps])

    const router = useMemo(() => {
        if (!allRoutes || allRoutes.length === 0) return null
        const routerArgs = [{ Component: LayoutWrapperMemo, children: allRoutes }]

        return overridePath
            ? createMemoryRouter(routerArgs, {
                  initialEntries: [
                      {
                          pathname: overridePath,
                          search: new URLSearchParams(overrideSearchParams ?? '').toString(),
                      },
                  ],
              })
            : createBrowserRouter(routerArgs)
    }, [overridePath, overrideSearchParams, allRoutes, LayoutWrapperMemo])

    if (allRoutes.length === 0 || !router) return null

    navigationService.setRoutes(allRoutes)

    return <RouterProvider router={router} />
}

interface LayoutWrapperOverrideProps extends LayoutOverrideProps {
    layoutOverride?: FunctionComponent
}

export const LayoutWrapper: FunctionComponent<LayoutWrapperOverrideProps> = memo(props => {
    const { layoutOverride } = props

    const navigate = useNavigate()
    const location = useLocation()
    const navigationType = useNavigationType()

    useScrollRestoration()

    const Element = useMemo(() => {
        logger.value.debug('Element memo recompute', Date.now() - window.loadMs)
        const { layoutOverride: _, ...normalLayoutOverrideProps } = props
        return layoutOverride ?? (() => <Layout {...normalLayoutOverrideProps} />)
    }, [props, layoutOverride])

    // Trailing slash not always added by React Router, but we always want to have one, especially for Cinemas, Popups and HelloBar
    useEffect(() => {
        if (!location.pathname.endsWith('/')) {
            location.pathname += '/'
            void navigate(location, { replace: true })
            logger.value.debug('Redirecting to path with trailing slash', location.pathname)
        }
    }, [location, navigate])

    navigationService.setNavigationType(navigationType)
    navigationService.setNavigate(navigate)
    navigationService.setLocation(location)

    return <Element />
})
